import React from "react";
import { Globe, Compass, Mountain, Waves, Trees, Milestone } from "lucide-react";
import SeoLandingTemplate from "./SeoLandingTemplate";

/**
 * /en/dnd-world-map · /it/dnd-world-map
 * SEO landing for the "D&D world map" query cluster.
 */
const data = {
  slug: "dnd-world-map",
  path: "/dnd-world-map",
  seoTitle: {
    en: "D&D World Map maker — build the whole continent for your campaign",
    it: "Creatore di mappe mondo D&D — costruisci un intero continente per la tua campagna",
  },
  seoDescription: {
    en: "Design a D&D world map that scales from continent to region to city. Cartomancer keeps every kingdom, biome and travel route inside one project — with DM-only lore and fog of war.",
    it: "Progetta una mappa mondo D&D che scala da continente a regione a città. Cartomancer tiene ogni regno, bioma e rotta di viaggio in un unico progetto — con lore riservata al DM e nebbia di guerra.",
  },
  eyebrow: { en: "Mytharion · World map maker", it: "Mytharion · Creatore mappe mondo" },
  h1: {
    en: "D&D World Map — one project for the whole continent",
    it: "Mappa mondo D&D — un progetto per l'intero continente",
  },
  lead: [
    {
      en: "A campaign world isn't a single map — it's continents, kingdoms, cities, roads and dungeons. Cartomancer lets you zoom from a whole continent down to a single dungeon room without losing your lore.",
      it: "Un mondo di campagna non è una singola mappa: sono continenti, regni, città, strade e dungeon. Cartomancer ti permette di passare dall'intero continente a una singola stanza di dungeon senza perdere la tua lore.",
    },
  ],
  primaryCta: {
    href: "/product/cartomancer_basic",
    label: { en: "Start your world", it: "Inizia il tuo mondo" },
  },
  secondaryCta: {
    href: "/cartomancer",
    label: { en: "See Cartomancer", it: "Vedi Cartomancer" },
  },
  featuresTitle: { en: "Built for world-scale campaigns", it: "Pensato per campagne di scala mondo" },
  features: [
    {
      icon: Globe,
      title: { en: "Continent → dungeon in one project", it: "Continente → dungeon in un solo progetto" },
      desc: {
        en: "Nest sub-maps: a marker on the continent can open a region, a city, a dungeon — as deep as your world goes.",
        it: "Sotto-mappe annidate: un marker sul continente può aprire una regione, una città, un dungeon — fin dove il tuo mondo si spinge.",
      },
    },
    {
      icon: Mountain,
      title: { en: "Biomes & terrain", it: "Biomi e terreni" },
      desc: {
        en: "Mountains, forests, deserts, tundra, marshes: assign biomes so travel modifiers and encounter tables make sense.",
        it: "Montagne, foreste, deserti, tundra, paludi: assegna i biomi così i modificatori di viaggio e le tabelle di incontro hanno senso.",
      },
    },
    {
      icon: Milestone,
      title: { en: "Roads & travel routes", it: "Strade e rotte di viaggio" },
      desc: {
        en: "Draw paths, compute travel time by mount or foot, add rest stops and merchant caravans.",
        it: "Disegna percorsi, calcola tempi di viaggio a piedi o in sella, aggiungi soste e carovane mercantili.",
      },
    },
    {
      icon: Waves,
      title: { en: "Oceans & rivers", it: "Oceani e fiumi" },
      desc: {
        en: "Water tiles with current direction and marine conditions modifiers — plan a naval campaign properly.",
        it: "Tile d'acqua con direzione delle correnti e modificatori di condizioni marine — pianifica una campagna navale come si deve.",
      },
    },
    {
      icon: Trees,
      title: { en: "Kingdoms & factions", it: "Regni e fazioni" },
      desc: {
        en: "Tag every settlement with its ruling faction, culture and language so political intrigue writes itself.",
        it: "Etichetta ogni insediamento con la fazione dominante, la cultura e la lingua: gli intrighi politici si scrivono da soli.",
      },
    },
    {
      icon: Compass,
      title: { en: "DM-only lore layer", it: "Livello lore solo per il DM" },
      desc: {
        en: "Prophecies, secret orders, hidden ruins — layer them on top of the world map and reveal only when the party earns it.",
        it: "Profezie, ordini segreti, rovine nascoste — sovrapponile alla mappa mondo e rivela solo quando i giocatori se lo meritano.",
      },
    },
  ],
  how: [
    {
      title: { en: "Sketch the continent", it: "Disegna il continente" },
      desc: {
        en: "Start big: coastlines, mountain ranges, major rivers. The rest can wait.",
        it: "Parti in grande: coste, catene montuose, fiumi principali. Il resto può aspettare.",
      },
    },
    {
      title: { en: "Populate kingdoms & cities", it: "Popola regni e città" },
      desc: {
        en: "Add capitals, borders, factions. Each city has its own map when your players show up there.",
        it: "Aggiungi capitali, confini, fazioni. Ogni città ha la sua mappa quando i giocatori la raggiungono.",
      },
    },
    {
      title: { en: "Zoom in as the story goes", it: "Ingrandisci man mano che la storia procede" },
      desc: {
        en: "You never need to prep every dungeon up front — build them just in time as the party travels.",
        it: "Non serve preparare ogni dungeon in anticipo — costruiscili just-in-time man mano che i giocatori viaggiano.",
      },
    },
  ],
  faq: [
    {
      q: { en: "Can I import an existing world map image?", it: "Posso importare l'immagine di una mappa mondo esistente?" },
      a: {
        en: "Yes. Upload any PNG/JPG as the base layer, then add markers, paths and DM notes on top.",
        it: "Sì. Carica qualsiasi PNG/JPG come livello base, poi aggiungi marker, percorsi e note DM sopra.",
      },
    },
    {
      q: { en: "Does it work for hex-crawl style D&D?", it: "Funziona per il D&D in stile hex-crawl?" },
      a: {
        en: "Yes. Hex overlays, per-hex terrain and travel modifiers are supported out of the box.",
        it: "Sì. Overlay esagonali, terreno e modificatori di viaggio per esagono sono supportati subito.",
      },
    },
    {
      q: { en: "How many world maps can I keep?", it: "Quante mappe mondo posso conservare?" },
      a: {
        en: "Basic keeps a small quota; Premium unlocks more slots and cloud-synced sub-maps.",
        it: "Basic ha una quota ridotta; Premium sblocca più slot e sotto-mappe sincronizzate sul cloud.",
      },
    },
  ],
  finalCta: {
    href: "/product/cartomancer_basic",
    headline: {
      en: "Give your D&D world the map it deserves",
      it: "Dai al tuo mondo D&D la mappa che merita",
    },
    label: { en: "Build your world", it: "Costruisci il tuo mondo" },
  },
  software: { name: "Cartomancer", category: "DesignApplication" },
};

export default function DndWorldMapLanding() {
  return <SeoLandingTemplate data={data} />;
}
